<template>
  <DefaultModalsContainer
    v-if="isOpen"
  >
    <template
      v-slot:heading
    >
        <div class="modal__head">
          <h2 v-if="isFormState" class="text-lg md:text-xl font-semibold text-gray-900">
            {{ $t('modal.winner.heading') }}
          </h2>
          <h2 v-if="isReviewState" class="text-lg md:text-xl font-semibold text-gray-900">
            {{ $t('modal.winner.heading-control') }}
          </h2>
          <h2 v-if="isSavedState" class="text-lg md:text-xl font-semibold text-gray-900">
            {{ $t('modal.winner.heading-complete') }}
          </h2>
          <button
            @click="closeModal"
            class="btn--icon p-1 hover:bg-gray-100 rounded-full transition-colors"
            type="button"
          >
            <DefaultIconsCross/>
          </button>
        </div>
    </template>

    <template
      v-slot:content
    >
        <div class="modal__body scrollbar-hide h-full">
          <div v-if="isFormState" class="modal__copy">
            <p class="text-sm">
              {{ $t('modal.winner.copy') }}
            </p>
            <p class="text-sm pt-2">
              {{ $t('modal.winner.copy-disclaimer') }}
            </p>
          </div>

          <div v-if="isReviewState" class="modal__copy">
            <p class="text-sm">
              {{ $t('modal.winner.copy-control') }}
            </p>
          </div>

          <div v-if="isSavedState" class="modal__copy">
            <p class="text-sm font-bold">
              {{ $t('modal.winner.copy-complete') }}
            </p>
            <p class="text-sm pt-2">
              {{ $t('modal.winner.copy-disclaimer-complete') }}
            </p>
          </div>


          <UForm v-if="isFormState || isReviewState" :validate="validate" :validate-on="['submit','change']" :state="state" class="modal__form" @submit="onSubmit" ref="form">
            <!-- Name -->
            <div class="field">
              <label for="name" class="block text-sm mb-1">
                {{ $t('modal.winner.input-label-name') }}<span>*</span>
              </label>
              <input
                id="name"
                v-model="state.name"
                type="text"
                :placeholder="$t('modal.winner.input-placeholder-name')"
                :disabled="isLoading || isReviewState"
                @change="updateValidation('name')"
              />
              <p v-for="error in getErrors('name')" class="text-xs leading-tight mt-1.5 text-primary-500">
                {{ error.message}}
              </p>
            </div>

            <!-- Street -->
            <div class="field">
              <label for="street" class="block text-sm mb-1">
                {{ $t('modal.winner.input-label-street') }}<span>*</span>
              </label>
              <input
                id="street"
                v-model="state.street"
                type="text"
                :placeholder="$t('modal.winner.input-placeholder-street')"
                :disabled="isLoading || isReviewState"
                @change="updateValidation('street')"
              />
              <p v-for="error in getErrors('street')" class="text-xs leading-tight mt-1.5 text-primary-500">
                {{ error.message}}
              </p>
            </div>

            <!-- City -->
            <div class="field">
              <label for="city" class="block mb-1">
                {{ $t('modal.winner.input-label-city') }}<span>*</span>
              </label>
              <input
                id="city"
                v-model="state.city"
                type="text"
                :placeholder="$t('modal.winner.input-placeholder-city')"
                :disabled="isLoading || isReviewState"
                @change="updateValidation('city')"
              />
              <p v-for="error in getErrors('city')" class="text-xs leading-tight mt-1.5 text-primary-500">
                {{ error.message}}
              </p>
            </div>

            <!-- PSČ Input -->
            <div class="field">
              <label for="psc" class="block mb-1">
                {{ $t('modal.winner.input-label-psc') }}<span>*</span>
              </label>
              <input
                id="psc"
                v-maska="'### ##'"
                v-model="state.psc"
                type="text"
                maxlength="6"
                :placeholder="$t('modal.winner.input-placeholder-psc')"
                :disabled="isLoading || isReviewState"
                @change="updateValidation('psc')"
              />
              <p v-for="error in getErrors('psc')" class="text-xs leading-tight mt-1.5 text-primary-500">
                {{ error.message}}
              </p>
            </div>

            <!-- Phone Input -->
            <div class="field">
              <label for="phone" class="block mb-1">
                {{ $t('modal.winner.input-label-phone') }}
              </label>
              <input
                id="phone"
                v-maska="'### ### ###'"
                v-model="state.phone"
                type="text"
                maxlength="11"
                :placeholder="$t('modal.winner.input-placeholder-phone')"
                :disabled="isLoading || isReviewState"
                @change="updateValidation('phone')"
              />
              <p v-for="error in getErrors('phone')" class="text-xs leading-tight mt-1.5 text-primary-500">
                {{ error.message}}
              </p>
            </div>

            <!-- Bankovní účet -->
            <div class="field">
              <label for="bankAccountNumber" class="block mb-1">
                {{ $t('modal.winner.input-label-bank-account') }}<span>*</span>
              </label>
              <input
                id="bankAccountNumber"
                v-model="state.bankAccountNumber"
                type="text"
                :placeholder="$t('modal.winner.input-placeholder-bank-account')"
                :disabled="isLoading || isReviewState"
                @change="updateValidation('bankAccountNumber')"
              />
              <p v-for="error in getErrors('bankAccountNumber')" class="text-xs leading-tight mt-1.5 text-primary-500">
                {{ error.message}}
              </p>
              <div class="mt-1 text-xs">
                {{ $t('modal.winner.input-disclaimer-bank-account') }}
              </div>
            </div>

            <div v-if="errorMessage && !errorMessageHide" class="text-primary-500">
              {{errorMessage}}
            </div>

            <div v-if="isFormState" class="modal__action">
              <button
                @click="closeModal"
                type="button"
                class="btn btn--border"
                :disabled="isLoading"
              >
                {{ $t('modal.winner.btn-cancel') }}
              </button>
              <button
                type="submit"
                class="btn btn--primary"
                :disabled="isLoading || !isFormValid"
                :class="{ 'btn--loading': isLoading }"
              >
                {{ $t('modal.winner.btn-continue') }}
              </button>
            </div>

            <div v-if="isReviewState" class="modal__action">
              <button
                @click="goBack"
                type="button"
                class="btn btn--border"
                :disabled="isLoading"
              >
                {{ $t('modal.winner.btn-edit') }}
              </button>
              <button
                type="submit"
                class="btn btn--primary"
                :class="{ 'btn--loading': isLoading }"
              >
                {{ $t('modal.winner.btn-submit') }}
              </button>
            </div>
          </UForm>

          <div v-if="isSavedState" class="modal__action">
            <button
              @click="closeModal"
              type="button"
              class="btn btn--primary"
              :disabled="isLoading"
            >
              {{ $t('modal.winner.btn-complete') }}
            </button>
          </div>

        </div>
    </template>
  </DefaultModalsContainer>
</template>

<script setup lang="ts">
import { reactive, defineProps, defineEmits, ref, type PropType } from 'vue'
import {WinningModalStep} from "~/libs/constants/WinningModalStep"
import type {FormError, FormSubmitEvent} from "#ui/types";
import { vMaska } from "maska/vue"
import {useI18n} from "vue-i18n";
const {t} = useI18n()

const props = defineProps({
  isOpen: { type: Boolean, required: false, default: false },
  isLoading: { type: Boolean, required: false, default: false },
  name: {type: String, required: false, default: null},
  surname: {type: String, required: false, default: null},
  psc: {type: String, required: false, default: null},
  step: {type: String as PropType<WinningModalStep>, required: false, default: WinningModalStep.FORM},
  errorMessage: { type: String, required: false, default: undefined },
})

const emits = defineEmits(['close', 'submit', 'goBack'])

const state = reactive({
  name: `${props.name} ${props.surname}`,
  street: "",
  city: "",
  psc: props.psc,
  phone: "",
  bankAccountNumber: "",
})


const changedValues = reactive({
  name: false,
  street: false,
  city: false,
  psc: false,
  phone: false,
  bankAccountNumber: false,
})

const valueChanged = (key: string) => {
  if (changedValues.hasOwnProperty(key)) {
    // @ts-ignore
    changedValues[key] = true
  }
}

const errorMessageHide = ref(false)
const validateAllFields = ref(false)

const form = ref()

const getErrors = (path?: string) => {
  if (!path) {
    return form.value?.getErrors()
  }
  return form.value?.getErrors().filter((error: any) => {
    return error.path === path
  })
}

const isFormState = computed(() => {
  return (props.step === WinningModalStep.FORM)
})
const isReviewState = computed(() => {
  return (props.step === WinningModalStep.REVIEW)
})
const isSavedState = computed(() => {
  return (props.step === WinningModalStep.SAVED)
})


const closeModal = (): void => {
  emits('close')
}

const goBack = (): void => {
  emits('goBack')
}
const updateValidation = async (key: string | undefined, allFields: boolean = false) => {
  if (key) {
    valueChanged(key)
  }
  try {
    validateAllFields.value = allFields
    await form.value?.validate()
    validateAllFields.value = false
  } catch (e) {}
}

const validate = (state: any): FormError[] => {
  errorMessageHide.value = true
  const errors = []

  // Name
  if (validateAllFields.value || changedValues.name) {
    if (!state.name) {
      errors.push({ path: 'name', message: t('modal.winner.error-name-required') })
    }
    const nameSplit = state.name?.split(' ')
    if (nameSplit?.length < 2) {
      errors.push({ path: 'name', message: t('modal.winner.error-name-missing') })
    }
  }

  // Street
  if ((validateAllFields.value || changedValues.street) && !state.street) {
    errors.push({ path: 'street', message: t('modal.winner.error-street-required') })
  }

  // City
  if ((validateAllFields.value || changedValues.city) && !state.city) {
    errors.push({ path: 'city', message: t('modal.winner.error-city-required') })
  }

  // psc
  if (validateAllFields.value || changedValues.psc) {
    if (!state.psc) {
      errors.push({path: 'psc', message: t('modal.winner.error-psc-required')})
    }
    const unmaskedPsc = unmaskPsc(state.psc)
    if (!unmaskedPsc || String(unmaskedPsc).length !== 5) {
      errors.push({path: 'psc', message: t('modal.winner.error-psc-number')})
    }
  }

  // Phone
  if ((validateAllFields.value || changedValues.phone) && state.phone) {
    const unmaskedPhone = unmaskPhone(state.phone)
    if (!unmaskedPhone || String(unmaskedPhone).length !== 9) {
      errors.push({path: 'phone', message: t('modal.winner.error-phone-number')})
    }
  }

  // Bank account number
  if ((validateAllFields.value || changedValues.bankAccountNumber) && !state.bankAccountNumber) {
    errors.push({ path: 'bankAccountNumber', message: t('modal.winner.error-bankAccountNumber-required') })
  }

  return errors
}

const unmaskPsc = (psc?: string): Number => {
  return Number(psc?.replace(/\s/g, ''))
}



const unmaskPhone = (phone?: string): Number => {
  return Number(phone?.replace(/\s/g, ''))
}

const isFormValid = computed(() => {
  return state.name && state.street && state.city && state.psc && state.bankAccountNumber && form.value?.getErrors()?.length === 0
  // return form.value?.getErrors()?.length === 0
})

const onSubmit = async (event: FormSubmitEvent<any>) => {
  if (props.isLoading) {
    return false
  }
  await updateValidation(undefined, true)
  if (!isFormValid.value) {
    return false
  }
  const payload = {
    name: event.data.name,
    street: event.data.street,
    city: event.data.city,
    psc: unmaskPsc(event.data.psc),
    phone: event.data.phone,
    bankAccountNumber: event.data.bankAccountNumber,
  }

  emits('submit', payload)

  errorMessageHide.value = false
}

</script>

<style scoped>
</style>